import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Target, Eye, Heart, Users, Globe, BookOpen, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const values = [
  { icon: Star, title: 'Excellence', description: 'Nous visons l\'excellence dans tout ce que nous faisons, de l\'enseignement à l\'accompagnement.' },
  { icon: Heart, title: 'Intégrité', description: 'L\'honnêteté et l\'éthique guident toutes nos actions et décisions.' },
  { icon: Users, title: 'Collaboration', description: 'Nous croyons en la force du travail d\'équipe et du partage de connaissances.' },
  { icon: Globe, title: 'Ouverture', description: 'Nous embrassons la diversité et l\'ouverture sur le monde.' }
];

const milestones = [
  { year: '2000', title: 'Fondation', description: 'Création de CEFAT à Abidjan avec une vision d\'excellence académique.' },
  { year: '2005', title: 'Expansion', description: 'Ouverture du Centre Qualifiant et premiers partenariats internationaux.' },
  { year: '2010', title: 'Certification', description: 'Obtention des certifications ISO et partenariat BIT.' },
  { year: '2015', title: 'Campus régionaux', description: 'Ouverture des campus de Bondoukou et Abengourou.' },
  { year: '2020', title: 'International', description: 'Lancement du campus d\'Accra et des séjours linguistiques.' },
  { year: '2024', title: 'Innovation', description: 'Digitalisation des formations et nouveaux programmes MBA.' }
];

const governance = [
  { name: 'Dr. Konan Yao', role: 'Président Fondateur', description: 'Expert en management avec 30 ans d\'expérience.' },
  { name: 'Prof. Aïcha Diallo', role: 'Directrice Académique', description: 'Docteure en sciences de l\'éducation.' },
  { name: 'M. Kwame Asante', role: 'Directeur Campus Accra', description: 'Spécialiste des relations internationales.' }
];

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO
        title="A Propos - Histoire & Valeurs de CEFAT"
        description="Decouvrez l'histoire de CEFAT Business School Abidjan : +20 ans d'excellence academique, 4 centres de formation, partenariat BIT, agree FDFP. Formation de qualite en Cote d'Ivoire."
        keywords="a propos CEFAT, histoire CEFAT Abidjan, ecole de commerce Cote d'Ivoire, valeurs CEFAT, partenariat BIT, FDFP agree, formation excellence Afrique"
        url="/a-propos"
      />
      {/* Hero */}
      <section className="relative bg-[#003366] py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#DC143C]/10 rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <span className="inline-block bg-[#FFD700] text-[#003366] px-4 py-2 rounded-full text-sm font-bold mb-6">
                À propos de nous
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
                L'Excellence <span className="text-[#FFD700]">Académique</span> en Afrique
              </h1>
              <p className="text-xl text-white/80 mb-8 leading-relaxed">
                Depuis plus de 20 ans, CEFAT Business School forme les leaders africains de demain. 
                Notre mission : démocratiser l'accès à une éducation de qualité internationale.
              </p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="hidden lg:block"
            >
              <img 
                src="https://images.unsplash.com/photo-1638636214032-581196ffd400?w=600&h=500&fit=crop"
                alt="Campus CEFAT"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div {...fadeInUp} className="bg-[#F8FAFC] p-8 rounded-2xl">
              <div className="w-14 h-14 bg-[#003366] rounded-xl flex items-center justify-center mb-6">
                <Eye className="text-white" size={28} />
              </div>
              <h2 className="font-heading text-2xl font-bold text-[#003366] mb-4">Notre Vision</h2>
              <p className="text-gray-600 leading-relaxed">
                Devenir la référence africaine en matière de formation professionnelle et académique, 
                en formant des leaders capables de transformer positivement leurs communautés et le continent.
              </p>
            </motion.div>

            <motion.div {...fadeInUp} className="bg-[#F8FAFC] p-8 rounded-2xl">
              <div className="w-14 h-14 bg-[#DC143C] rounded-xl flex items-center justify-center mb-6">
                <Target className="text-white" size={28} />
              </div>
              <h2 className="font-heading text-2xl font-bold text-[#003366] mb-4">Notre Mission</h2>
              <p className="text-gray-600 leading-relaxed">
                Offrir des formations de qualité internationale, accessibles et adaptées aux réalités 
                africaines, tout en favorisant l'insertion professionnelle et l'entrepreneuriat.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="badge badge-blue mb-4">Nos Valeurs</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#003366]">
              Ce qui nous guide
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-md text-center card-hover"
              >
                <div className="w-14 h-14 bg-[#003366]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <value.icon className="text-[#003366]" size={28} />
                </div>
                <h3 className="font-heading text-lg font-bold text-[#003366] mb-2">{value.title}</h3>
                <p className="text-gray-600 text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="badge badge-red mb-4">Notre Histoire</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#003366]">
              Plus de 20 ans d'excellence
            </h2>
          </motion.div>

          <div className="relative">
            <div className="absolute left-1/2 -translate-x-1/2 h-full w-0.5 bg-gray-200 hidden md:block" />
            
            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative flex items-center mb-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
              >
                <div className={`w-full md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                  <div className="bg-[#F8FAFC] p-6 rounded-xl">
                    <span className="font-heading text-3xl font-bold text-[#DC143C]">{milestone.year}</span>
                    <h3 className="font-heading text-xl font-bold text-[#003366] mt-2">{milestone.title}</h3>
                    <p className="text-gray-600 mt-2">{milestone.description}</p>
                  </div>
                </div>
                <div className="absolute left-1/2 -translate-x-1/2 w-4 h-4 bg-[#003366] rounded-full hidden md:block" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Distinctions */}
      <section className="py-20 bg-[#003366]">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="badge bg-[#FFD700] text-[#003366] mb-4">Distinctions</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">
              Nos Reconnaissances
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { label: 'Partenaire BIT', desc: 'Bureau International du Travail' },
              { label: 'Agréé FDFP', desc: 'Fonds de Formation Professionnelle' },
              { label: 'Certifié ISO', desc: 'Qualité internationale' },
              { label: 'Réseau ABSBS', desc: 'African Business Schools' }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/10 backdrop-blur p-6 rounded-xl text-center"
              >
                <Award className="text-[#FFD700] mx-auto mb-4" size={40} />
                <h3 className="font-heading font-bold text-white mb-1">{item.label}</h3>
                <p className="text-white/70 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Governance */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="badge badge-blue mb-4">Gouvernance</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#003366]">
              Notre Équipe Dirigeante
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {governance.map((person, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl overflow-hidden shadow-md text-center"
              >
                <div className="h-48 bg-gradient-to-br from-[#003366] to-[#001a33] flex items-center justify-center">
                  <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center">
                    <span className="font-heading text-3xl font-bold text-[#003366]">
                      {person.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-bold text-[#003366]">{person.name}</h3>
                  <p className="text-[#DC143C] font-medium mb-2">{person.role}</p>
                  <p className="text-gray-600 text-sm">{person.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#DC143C]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Rejoignez l'aventure CEFAT
          </h2>
          <p className="text-white/90 mb-8 text-lg">
            Découvrez nos formations et construisez votre avenir avec nous.
          </p>
          <Link to="/preinscription">
            <Button className="bg-white text-[#DC143C] hover:bg-gray-100 rounded-full px-10 py-6 text-lg font-bold shadow-lg">
              Je me préinscris <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
